import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { makeStyles, Grid, Typography } from "@material-ui/core";

import { PAGES } from "../../";
import { KPIS } from "../constants";
import { PlusButton } from "../../../components/base";
import { ComposedChart } from "../../../components/charts";
import { ColorIndicator } from "../../../components/colorIndicator";
import { CHART_KEYS, BAR_CHART_COLORS } from "../constants";
import { noiPowerRevenueLegends, noiPowerRevenueBars } from "../sample";
import {
  api,
  getPercent,
  getLastBarTotal,
  currencyFormatter,
  requestDateFormat,
  sumFromListOfObjects,
  generateEstimatedData,
  isResponseDataPerMonth,
} from "../../../services";
import useDiff from "../../../hooks/useDiff";
import { NoDataCard } from "../../../components/parts";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "80%",
    paddingLeft: theme.spacing(4),
  },
  row: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& p": {
      fontSize: 12,
    },
  },
  title: {
    marginBottom: theme.spacing(1.5),
  },
}));

const LINE = {
  dataKey: "noi_power_revenue_percent",
  stroke: "#ff7300",
};

export const NOIChart = ({ activeKpi, period, startDate, endDate }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [legends, setLegends] = useState(noiPowerRevenueLegends);
  const [diffDays] = useDiff(startDate, endDate);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  const handleClick = () => {
    navigate(`${PAGES.dashboard.index}/${PAGES.dashboard.noi}`);
  };

  useEffect(() => {
    let isDataPerMonth = true;
    let data = [];
    if (rawData) {
      const legends = noiPowerRevenueLegends.map((legend) => ({
        ...legend,
        value: 0,
        percentage: 0,
      }));

      isDataPerMonth = isResponseDataPerMonth(rawData.results);
      const currentYear = new Date().getUTCFullYear();

      data = rawData.results.map((item) => {
        const date = new Date(item.date);
        const year = date.getUTCFullYear();
        if (year > currentYear) {
          return {
            date: item.date,
            estimated: item.trading + item.battery + item.ev,
            noi_power_revenue_percent: item.noi_power_revenue_percent,
            x: `${isDataPerMonth ? `${date.getMonth() + 1}-` : ""}${date.getUTCFullYear()}-E*`,
          };
        }

        return {
          date: item.date,
          power_revenue: item.power_revenue,
          trading: item.trading,
          battery: item.battery,
          noi_power_revenue_percent: item.noi_power_revenue_percent,
          ev: item.ev,
          x: `${isDataPerMonth ? `${date.getMonth() + 1}-` : ""}${date.getUTCFullYear()}`,
        };
      });

      const fields = ["trading", "battery", "ev"];
      fields.forEach((item, i) => {
        legends[i].value = sumFromListOfObjects(data, item);
        legends[i].valueDisplay = legends[i].value;
      });

      const total = sumFromListOfObjects(data, "power_revenue");
      legends.forEach((legend) => {
        if (legend.key === CHART_KEYS.noi_with_power_revenue) {
          legend.value = rawData.noi_with_power_revenue;
          legend.valueDisplay = legend.value;
          legend.percentage = rawData.noi_with_power_revenue_percent;
        } else {
          legend.percentage = getPercent(legend.value, total);
        }
      });

      setLegends(legends);
    }

    const lastBarTotal = getLastBarTotal(data, ["trading", "battery", "ev"]);
    const rawEstimatedData = generateEstimatedData(data, lastBarTotal, isDataPerMonth, undefined);
    const estimatedData = rawEstimatedData.map((estimatedData, i) => ({
      ...estimatedData,
      noi_power_revenue_percent: data.length
        ? data[data.length - 1].noi_power_revenue_percent + 0.05 * (i + 1)
        : Math.random(),
    }));
    setData(data.concat(estimatedData));
  }, [rawData]);

  useEffect(() => {
    if (activeKpi === KPIS.noi_increase) {
      const getData = (url) => {
        api.get(url).then((resp) => setRawData(resp.data));
      };

      const getUrl = (period) => {
        let url = "/praas/v1/dashboard/noi-graph/";
        if (period.name === "RANGE" && endDate) {
          url += `?from_date=${requestDateFormat(startDate)}&to_date=${requestDateFormat(endDate)}`;
          getData(url);
        } else if (["YTD", "Acc.", "20Y"].includes(period.name)) {
          url += `?date_filter=${period.filter}`;
          getData(url);
        }
      };

      getUrl(period);
    }
  }, [activeKpi, period, startDate, endDate]);

  return (
    <>
      <Grid className={classes.title} container alignItems="center">
        <Typography
          style={{
            fontWeight: "bold",
            marginRight: 25,
          }}
        >
          NOI Increase With Power Revenues
        </Typography>
      </Grid>
      {rawData?.results?.length ? (
        <Grid container>
          <Grid item xs={9} xl={10}>
            <ComposedChart
              top={5}
              period={period}
              chartData={data}
              bars={noiPowerRevenueBars}
              line={LINE}
              dateDiff={diffDays}
            />
          </Grid>
          <Grid item xs={3} xl={2}>
            <Grid
              className={classes.container}
              container
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={10}>
                {legends.map((legend, i) => (
                  <Grid
                    className={classes.row}
                    style={{ paddingTop: `${legends.length - 1 === i ? 10 : 0}px` }}
                    key={i}
                    container
                    alignItems="center"
                    justifyContent="space-evenly"
                  >
                    <Grid item xs={7}>
                      <Grid container alignItems="center">
                        <ColorIndicator
                          color={BAR_CHART_COLORS[legend.key]}
                          onlyBorder={legend.key === CHART_KEYS.total_unrealized}
                        />
                        <Typography>{legend.text}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>
                        {currencyFormatter(legend.valueDisplay, currencySymbol)}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography>{legend.percentage}%</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={2}>
                <PlusButton onClick={handleClick} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid>
          <NoDataCard rootStyle={{ minHeight: 200 }} gridStyle={{ height: 120 }} />
        </Grid>
      )}
    </>
  );
};
